/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { memo, useMemo } from 'react';
import {
	Placement as Nav4ProjectsSection,
	SSRPlacement as Nav4ProjectsSectionSSR,
} from '@post-office/placements/nav-4-projects-section/frontend';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { postOfficeNav4ProjectsSectionResource } from '@jira/router-resources__post-office/src/conditionalPostOfficeJiraTopBannerResource.tsx';
import { componentWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import { PlacementFetchProvider } from '@jira/post-office__post-office-provider/src/placement-fetch-provider.tsx';
import { usePlacementPreloadedData } from '@jira/post-office__post-office-provider-preload-placement/src/preload-placement.tsx';
import { useIsClientReady } from '@jira/ssr__ssr-toolkit/src/hooks/use-is-client-ready.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment';
import { fg } from '@atlassian/jira-feature-gating';
import type { DefaultMessagesResponse } from '@post-office/placement-common-v2/types';

const Nav4ProjectsSectionPlacementOld: React.MemoExoticComponent<() => React.JSX.Element | null> =
	memo((): React.JSX.Element | null => {
		// Atlaspack couldn't bundle Post Office placement, so we need to return null here
		// https://atlassian.slack.com/archives/C0288U06PGC/p1755502530582239
		const isClientReady = useIsClientReady();
		if (!isClientReady) {
			return null;
		}

		return (
			<UFOSegment name="jira-growth-nav-4-projects-section">
				<JSErrorBoundary
					id="growthNav4ProjectsSection"
					packageName="@jira/growth__nav-4-projects-section"
					teamName="Sequoia"
					fallback="unmount"
				>
					<Nav4ProjectsSection />
				</JSErrorBoundary>
			</UFOSegment>
		);
	});

const Nav4ProjectsSectionPlacementNew: React.MemoExoticComponent<() => React.JSX.Element> = memo(
	(): React.JSX.Element => {
		const [preloadedInitialData] = usePlacementPreloadedData(postOfficeNav4ProjectsSectionResource);
		const memoizedPlacementProps = useMemo(
			() => ({
				// oxlint-disable-next-line typescript/consistent-type-assertions
				preloadedInitialData: preloadedInitialData as DefaultMessagesResponse | undefined,
			}),
			[preloadedInitialData],
		);

		return (
			<UFOSegment name="jira-growth-nav-4-projects-section">
				<JSErrorBoundary
					id="growthNav4ProjectsSection"
					packageName="@jira/growth__nav-4-projects-section"
					teamName="Sequoia"
					fallback="unmount"
				>
					<PlacementFetchProvider resource={postOfficeNav4ProjectsSectionResource}>
						<Nav4ProjectsSectionSSR {...memoizedPlacementProps} />
					</PlacementFetchProvider>
				</JSErrorBoundary>
			</UFOSegment>
		);
	},
);

export const Nav4ProjectsSectionPlacement: React.FC = componentWithCondition(
	() => fg('enable_ssr_recommendations_in_nav4'),
	Nav4ProjectsSectionPlacementNew,
	Nav4ProjectsSectionPlacementOld,
);
